(ns ui.view.transaction-process.components.action-bar
  (:require
   [common.localization :refer [tr]]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.state.session.sub :as session-sub]
   [ui.view.transaction-process.components.navigation :as navigation]
   [ui.view.transaction-process.event :as event]
   [ui.view.transaction-process.modals.transition.event :as transition-event]
   [ui.view.transaction-process.modals.transition.model :as transition-model]
   [ui.view.transaction-process.modals.transition.sub :as transition-sub]
   [ui.view.transaction-process.modals.transition.view :as transition-view]
   [ui.view.transaction-process.model :as model]
   [ui.view.transaction-process.sub :as sub]
   [ui.view.transaction-process.util :as tp-util]))

(defn- find-transition
  [transitions current-stage]
  (->> transitions
       (filter #(= (:transition/from %) current-stage))
       first))

(def ^:private dev-global-role-options
  [[:admin "Admin"] [:user "User"] [:read-only "Read-only"]])

(def ^:private dev-team-role-options
  [[:investment-team "IT"] [:platform-team "PT"] [:portfolio-manager "PM"]])

(defn- dev-chip
  [{:keys [active? on-click highlight]} label]
  [:button
   {:class    (str "px-2 py-1 text-xs m-0 rounded border "
                   (cond
                     (and active? (= highlight :warning)) "border-yellow-400 bg-yellow-50 text-yellow-700"
                     active?                               "border-primary-regular bg-primary-regular/10 text-primary-regular"
                     :else                                 "border-gray-300 bg-white text-gray-500"))
    :on-click on-click}
   label])

(defn- dev-tools-panel
  "Body of the dev-tools popover: skip-validation toggle + frontend role/team
   impersonation. Impersonation overrides only the form's permission inputs — it
   does not change backend enforcement."
  []
  (let [skip?       @(rf/subscribe [::sub/skip-mandatory-validation?])
        imp         @(rf/subscribe [::sub/impersonation])
        active?     (:active? imp)
        global-role (:global-role imp)
        team-roles  (set (:team-roles imp))
        set-imp!    (fn [v] (rf/dispatch [::event/set-impersonation v]))]
    [:div {:class "flex flex-col gap-3 w-64"}
     [:span {:class "text-xs m-0 font-semibold text-gray-700"} "Dev tools"]
     [:div {:class "flex items-center justify-between gap-2"}
      [:span {:class "text-xs m-0 text-gray-500"} "Skip mandatory validation"]
      [dev-chip
       {:active?   skip?
        :highlight :warning
        :on-click  #(rf/dispatch [::event/toggle-skip-mandatory-validation])}
       (if skip? "ON" "OFF")]]
     [:div {:class "flex flex-col gap-2 pt-3 border-t border-gray-200"}
      [:span {:class "text-xs m-0 text-gray-400"} "Impersonate role"]
      [:div {:class "flex items-center gap-1 flex-wrap"}
       [dev-chip {:active? (not active?) :highlight :warning :on-click #(set-imp! nil)} "Off"]
       (doall
        (for [[role label] dev-global-role-options]
          ^{:key role}
          [dev-chip
           {:active?  (and active? (= global-role role))
            :on-click #(set-imp! {:active?     true
                                  :global-role role
                                  :team-roles  (if active? team-roles #{})})}
           label]))]
      ;; Team membership is an INDEPENDENT axis: shown for every global role so you
      ;; can test admin / user / read-only crossed with any combination of TP team
      ;; memberships. (Admin still resolves :w regardless of team — that's the real
      ;; behavior — but other team-dependent UI reflects the selection.)
      (when active?
        [:div {:class "flex flex-col gap-1"}
         [:span {:class "text-xs m-0 text-gray-400"} "Team membership"]
         (into [:div {:class "flex items-center gap-1 flex-wrap"}]
               (for [[role label] dev-team-role-options]
                 ^{:key role}
                 [dev-chip
                  {:active?  (contains? team-roles role)
                   :on-click #(set-imp! (assoc imp :team-roles
                                               (if (contains? team-roles role)
                                                 (disj team-roles role)
                                                 (conj team-roles role))))}
                  label]))])]]))

(defn- dev-tools
  "DEV-only single button (gated by :feature/transaction-process-dev-mode) that opens
   an upward popover holding all dev tools. The trigger highlights when any override
   (skip-validation or impersonation) is active."
  []
  (let [open? (r/atom false)]
    (fn []
      (let [skip?       @(rf/subscribe [::sub/skip-mandatory-validation?])
            active?     (:active? @(rf/subscribe [::sub/impersonation]))
            any-active? (or skip? active?)]
        [popover/view
         {:open?          @open?
          :on-open-change #(reset! open? %)
          :direction      :top
          :content-class  "tw2 rounded-md p-4 bg-white border border-gray-200 shadow-lg"}
         [:button
          {:class (str "px-2 py-1 text-xs m-0 rounded border "
                       (if any-active?
                         "border-yellow-400 bg-yellow-50 text-yellow-700"
                         "border-gray-300 bg-white text-gray-500"))}
          (if any-active? "DEV ●" "DEV")]
         [dev-tools-panel]]))))

(defn- resolve-attachments
  [entity-data]
  (->> (:common/attachments entity-data)
       (map (fn [ref] @(rf/subscribe [::session-sub/entity (or (:db/id ref) ref)])))
       (remove nil?)
       (remove :common/disabled?)))

(defn view
  [{:keys [ctx current-stage transitions entity-data
           on-save! visible-fields-fn saving? dirty?]}]
  (let [transition (find-transition transitions current-stage)
        close-action? (:transition/close-action? transition)
        reject-action? (:transition/reject-action? transition)
        skip-validation? @(rf/subscribe [::sub/skip-mandatory-validation?])
        read-only? @(rf/subscribe [::sub/effective-read-only?])
        can-write-status? @(rf/subscribe [::sub/can-write-field?
                                          ctx
                                          :transaction-process/status])
        can-reopen? @(rf/subscribe [::sub/can-reopen? ctx])
        closing-status-read-only? (and (= :closing current-stage)
                                       (not can-write-status?))
        terminal? (model/terminal? entity-data)
        missing (when (and (not skip-validation?) visible-fields-fn)
                  (tp-util/missing-mandatory-fields
                   (visible-fields-fn) entity-data (resolve-attachments entity-data)))
        blocking-sub-entities (when (and (not skip-validation?) transition (not close-action?))
                                @(rf/subscribe [::sub/blocking-sub-entities-for-transition ctx transition]))
        forward-blocked? (boolean (or (seq missing) (seq blocking-sub-entities)))
        open-transition! (fn [selected-transition]
                           (let [reject?   (= :reject (:transition/action selected-transition))
                                 backward? (= :backward (:transition/direction selected-transition))]
                             (rf/dispatch [::transition-event/open ctx
                                           {:transition            selected-transition
                                            :value-lookup          entity-data
                                            :missing               (when-not (or reject? backward?) missing)
                                            :blocking-sub-entities (when-not (or reject? backward?)
                                                                     blocking-sub-entities)}])))]
    [:<>
     [:div {:class "flex items-center justify-between px-6 py-3 pr-24 bg-white border-t border-gray-200"}
      [:div {:class "flex items-center gap-4"}
       [button/sm-secondary-hollow
        {:disabled? saving?
         ;; Same as the header back-arrow: leave to the list, confirming first
         ;; (via the confirmation modal) if there are unsaved changes.
         :on-click  (fn [] (navigation/leave-to-list! dirty?))}
        (tr :continue-later)]
       (when-not terminal?
         (when-let [back-label (:transition/backward-label transition)]
           [button/sm-secondary-hollow
            {:disabled? (or saving? read-only?)
             :on-click  (fn []
                          (open-transition!
                           (transition-model/as-backward-transition transition)))}
            back-label]))
       (when (and (not terminal?) reject-action?)
         [button/sm-danger-solid
          {:disabled? (or saving? read-only? closing-status-read-only?)
           :on-click  (fn []
                        (open-transition!
                         {:transition/from          current-stage
                          :transition/action        :reject
                          :transition/reject-action? true}))}
          (tr :transaction-process/reject-transaction)])]
      [:div {:class "flex items-center gap-3"}
       (when (session/has-feature-access? :feature/transaction-process-dev-mode)
         [dev-tools])
       (if terminal?
         ;; Reopening is a Platform Team action. Roles without it (Investment
         ;; Team, Portfolio Manager) get no button rather than a dead disabled
         ;; one. DEV-8150.
         (when can-reopen?
           [button/sm-secondary-hollow
            {:disabled? (or saving? read-only?)
             :on-click  (fn [] (rf/dispatch [::event/reopen-transaction ctx]))}
            (tr :transaction-process/reopen-transaction)])
         [:<>
          (when-not read-only?
            [button/sm-secondary-hollow
             {:disabled? (or saving? (not dirty?))
              :on-click  (fn [] (on-save! nil))}
             (if saving?
               (tr :saving)
               (tr :transaction-process/save-draft))])
          (cond
            close-action?
            [button/sm-primary-solid
             {:disabled? (or forward-blocked? read-only? (not can-write-status?))
              :tooltip   (when forward-blocked?
                           (tp-util/forward-blockers-tooltip
                            {:missing-fields missing
                             :blocking-sub-entities blocking-sub-entities}))
              :on-click  (fn [] (open-transition! transition))}
             (:transition/close-label transition)]

            (:transition/forward-label transition)
            [button/sm-primary-solid
             {:disabled? (or forward-blocked? read-only?)
              :tooltip   (when forward-blocked?
                           (tp-util/forward-blockers-tooltip
                            {:missing-fields missing
                             :blocking-sub-entities blocking-sub-entities}))
              :on-click  (fn [] (open-transition! transition))}
             (:transition/forward-label transition)])])]]
     (when @(rf/subscribe [::transition-sub/open? ctx])
       [transition-view/view {:ctx ctx}])]))
